// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react'; // Added useCallback
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useLocale from '../hooks/useLocale'; // Adjust path if needed
import './reset-password.css'; // Assuming kebab-case: reset-password.css

function ResetPassword() {
    // --- Get 't' function ---
    const { t } = useLocale();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // State variables
    const [token, setToken] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Effect to get token
    useEffect(() => {
        const resetToken = searchParams.get('token');
        if (resetToken) {
            setToken(resetToken);
            console.log(`[ResetPassword] Token found in URL: ${resetToken}`);
        } else {
            console.error("[ResetPassword] No token found in URL.");
            // --- Use t() for error message ---
            setMessage(t('auth_error_token_missing', 'Invalid password reset link: No token provided.'));
        }
    }, [searchParams, navigate, t]); // Removed strings, added t

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setIsLoading(true);

        // --- Frontend Validations using t() ---
        if (!password || !confirmPassword) {
            setMessage(t('auth_error_password_required', 'Please enter and confirm your new password.'));
            setIsLoading(false);
            return;
        }
        if (password !== confirmPassword) {
            setMessage(t('auth_error_password_mismatch', 'Passwords do not match.'));
            setIsLoading(false);
            return;
        }
        const minPasswordLength = 8;
        if (password.length < minPasswordLength) {
            // Use t() for length error - simple version for now
             setMessage(t('auth_error_password_too_short', `Password must be at least 8 characters long.`));
            // If your 't' function supports interpolation later:
            // setMessage(t('auth_error_password_too_short_interp', 'Password must be at least {{min}} characters long.', { min: minPasswordLength }));
            setIsLoading(false);
            return;
        }

        try {
            // --- API Call (Stays the same) ---
            console.log(`[ResetPassword] Sending request to /api/reset-password for token: ${token}`);
            const response = await axios.post('/api/reset-password', {
                token: token,
                newPassword: password
            });
    
            // --- Handle Success (UPDATED) ---
            // Get message code from backend, provide fallback key if needed
            const messageCode = response.data?.messageCode || 'API_AUTH_PASSWORD_RESET_SUCCESS';
            setMessage(
                t(messageCode, 'Password has been reset successfully. Redirecting to login...') // Use code with t()
            );
            setPassword(''); // Clear password fields
            setConfirmPassword('');
            // Redirect after delay (Stays the same)
            setTimeout(() => {
                navigate('/login?reset=success');
            }, 3000);
    
        } catch (error) {
            // --- Handle Error (UPDATED) ---
            console.error("Reset password API call failed:", error.response || error);
            // Get specific error code from backend response
            const errorCode = error.response?.data?.errorCode;
            // Use the specific code with t(), or a generic fallback key if no code provided
            const messageKey = errorCode || 'API_SERVER_ERROR_GENERIC'; // Or AUTH_ERROR_GENERIC_REQUEST_FAILED
    
            setMessage(
                t(messageKey, 'Password reset failed. Please try again later.') // Use key with t(), provide final fallback text
            );
    
        } finally {
            // --- Manage Loading State (SIMPLIFIED) ---
            // Always set loading to false after try or catch finishes
            setIsLoading(false);
        }
    }; // --- End of handleSubmit ---

    // --- Rendering Logic using t() ---
    return (
        <div>
            <h2>{t('auth_reset_password_title', 'Reset Your Password')}</h2>

            {!token && message && (
                <p className="form-message error">{message}</p>
            )}

            {token && (
                <form onSubmit={handleSubmit} className="reset-password-form">
                    <div className="form-group">
                        <label htmlFor="password">{t('auth_reset_password_new_label', 'New Password')}:</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">{t('auth_reset_password_confirm_label', 'Confirm New Password')}:</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <button type="submit" disabled={isLoading}>
                         {/* Use t() for button text */}
                        {isLoading ? t('common_loading_button_resetting', 'Resetting...') : t('auth_reset_password_submit_button', 'Reset Password')}
                    </button>
                </form>
            )}

            {token && message && (
                // Use generic classes, specific text comes from message state set using t()
                <p className={`form-message ${message.includes('Failed') || message.includes('Invalid') || message.includes('enter') || message.includes('match') ? 'error' : 'success'}`}>
                    {message}
                </p>
            )}

            {!isLoading && (
                 <p className="form-link-container">
                    <Link to="/login" className="app-link">
                        {/* Use t() for link text */}
                        {t('common_back_to_login', 'Back to Login')}
                    </Link>
                </p>
            )}
        </div>
    );
}

export default ResetPassword;